import {Show} from 'platform/foundation';

import {isNil, isNotNil} from 'ramda';

import {SaveSaleVehicleFinancingBody, VehicleFinancingType} from '@dms/api/vehicle';
import {isValidBalloonCombination} from '@dms/features/vehicle-financing-template';

import {Nullish} from 'shared/web';

import {FinancingCellCoordinates} from '../types/FinancingCellCoordinates';
import {VehicleFinancingPricing} from '../types/VehicleFinancingPricing';
import {FinancingInsurance} from '../utils/calculateFinancingOffer';
import {findFinancingCell} from '../utils/findFinancingCell';
import {removeFinancingCell} from '../utils/removeFinancingCell';
import {replaceBalloonFinancingVariants} from '../utils/replaceBalloonFinancingVariants';
import {setActiveFinancingCell} from '../utils/setActiveFinancingCell';
import {updateFinancingCell} from '../utils/updateFinancingCell';
import {BalloonFinancingCellDialog} from './BalloonFinancingCellDialog';
import {FinancingCellDialog} from './FinancingCellDialog';

interface FinancingCellEditorDialogProps {
  type: VehicleFinancingType;
  cellCoordinates: FinancingCellCoordinates | Nullish;
  vehicleFinancingBody: SaveSaleVehicleFinancingBody | Nullish;
  balloonPayments: number[];
  activeBalloon: number;
  vehiclePrice: number;
  currency: string;
  pricing: VehicleFinancingPricing | null;
  isReadOnly: boolean;
  onChange: (vehicleFinancingBody: SaveSaleVehicleFinancingBody) => void;
  onSelectedBalloonChange: (balloonPayment: number | null) => void;
  onClose: () => void;
}

/** The dialog that edits one matrix position — all of its balloon variants at once for BALLOON. */
export function FinancingCellEditorDialog(props: FinancingCellEditorDialogProps) {
  const cellCoordinates = props.cellCoordinates;
  const vehicleFinancingBody = props.vehicleFinancingBody;

  if (isNil(cellCoordinates) || isNil(vehicleFinancingBody)) {
    return null;
  }

  const isBalloonEditor = props.type === 'BALLOON';
  const insurance: FinancingInsurance = {
    included: vehicleFinancingBody.insuranceIncludedInCalculation,
    liabilityMonthly: vehicleFinancingBody.liabilityInsuranceMonthly,
    comprehensiveMonthly: vehicleFinancingBody.comprehensiveInsuranceMonthly,
  };
  const isActiveVariantPosition =
    vehicleFinancingBody.activeTerm === cellCoordinates.term &&
    vehicleFinancingBody.activeDownPayment === cellCoordinates.downPayment;

  return (
    <>
      <Show when={isBalloonEditor}>
        <BalloonFinancingCellDialog
          key={`${cellCoordinates.term}-${cellCoordinates.downPayment}-balloon`}
          isOpen
          term={cellCoordinates.term}
          downPayment={cellCoordinates.downPayment}
          balloonPayments={props.balloonPayments.filter((balloonPayment) =>
            isValidBalloonCombination(cellCoordinates.downPayment, balloonPayment)
          )}
          initialBalloonPayment={cellCoordinates.balloonPayment ?? props.activeBalloon}
          activeBalloonPayment={
            isActiveVariantPosition ? vehicleFinancingBody.activeBalloonPayment : null
          }
          cells={vehicleFinancingBody.rateMatrix.cells.filter(
            (cell) =>
              cell.term === cellCoordinates.term &&
              cell.downPayment === cellCoordinates.downPayment &&
              isNotNil(cell.balloonPayment)
          )}
          vehiclePrice={props.vehiclePrice}
          currency={props.currency}
          pricing={props.pricing}
          insurance={insurance}
          isReadOnly={props.isReadOnly}
          onClose={props.onClose}
          onSave={(cells, activeBalloonPayment) => {
            props.onChange(
              replaceBalloonFinancingVariants(
                vehicleFinancingBody,
                cellCoordinates.term,
                cellCoordinates.downPayment,
                cells,
                activeBalloonPayment
              )
            );
            props.onSelectedBalloonChange(activeBalloonPayment ?? cellCoordinates.balloonPayment);
            props.onClose();
          }}
          onDelete={() => {
            props.onChange(
              replaceBalloonFinancingVariants(
                vehicleFinancingBody,
                cellCoordinates.term,
                cellCoordinates.downPayment,
                [],
                null
              )
            );
            props.onClose();
          }}
        />
      </Show>
      <Show when={!isBalloonEditor}>
        <FinancingCellDialog
          key={`${cellCoordinates.term}-${cellCoordinates.downPayment}-${cellCoordinates.balloonPayment}`}
          isOpen
          type={props.type}
          term={cellCoordinates.term}
          downPayment={cellCoordinates.downPayment}
          balloonPayment={cellCoordinates.balloonPayment}
          cell={findFinancingCell(vehicleFinancingBody.rateMatrix.cells, cellCoordinates)}
          vehiclePrice={props.vehiclePrice}
          currency={props.currency}
          pricing={props.pricing}
          insurance={insurance}
          isActive={
            isActiveVariantPosition &&
            vehicleFinancingBody.activeBalloonPayment === cellCoordinates.balloonPayment
          }
          isReadOnly={props.isReadOnly}
          onClose={props.onClose}
          onSave={(cell, setActive) => {
            props.onChange(
              setActiveFinancingCell(
                updateFinancingCell(vehicleFinancingBody, cell),
                cell,
                setActive
              )
            );
            props.onClose();
          }}
          onDelete={(cell) => {
            props.onChange(removeFinancingCell(vehicleFinancingBody, cell));
            props.onClose();
          }}
        />
      </Show>
    </>
  );
}
